import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import StatusChip from "../../components/StatusChip";
import { projectStatusTone } from "../../status/StatusChipTones";
import { formatEnum } from "../../utils/formatEnum";
import DeleteTaskModal from "../../components/DeleteTaskModal";
import ThemedDataGrid from "../../components/ThemedDataGrid";

import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";

import { graphqlEndpoint } from "../../config";
import { READ_ALL_TASKS } from "../../graph/tasks/queries";
import { DELETE_TASK } from "../../graph/tasks/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const ManageTasks = () => {
  const navigate = useNavigate();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [tasks, setTasks] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const fetchTasks = useCallback(async () => {
    if (!tenantId) {
      setTasks([]);
      return;
    }

    try {
      const data = await client.request(READ_ALL_TASKS, {
        tenantId,
        unlinkedOnly: true,
        includeArchived: false,
      });

      const formattedTasks = (data.readAllTasks || []).map((task) => ({
        ...task,
        id: task.id,
        accountableRoleName: task.accountableRole?.name || "—",
        assignedTenantUserName: task.assignedTenantUser?.user
          ? `${task.assignedTenantUser.user.firstName || ""} ${task.assignedTenantUser.user.lastName || ""}`.trim() ||
            task.assignedTenantUser.user.email ||
            "—"
          : "—",
      }));

      setTasks(formattedTasks);
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
      setTasks([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load tasks",
      });
    }
  }, [tenantId, setSnackbar]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleOpenDeleteModal = (task) => {
    setSelectedTask(task);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedTask(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTask?.id) return;

    try {
      await client.request(DELETE_TASK, { id: selectedTask.id });
      await fetchTasks();
      handleCloseDeleteModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Task deleted successfully",
      });
    } catch (err) {
      console.error("Failed to delete task:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete task",
      });
    }
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 1.4 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ value }) => (
        <StatusChip
          label={formatEnum(value)}
          tone={projectStatusTone(value)}
        />
      ),
    },
    { field: "accountableRoleName", headerName: "Accountable Role", flex: 1.1 },
    { field: "assignedTenantUserName", headerName: "Assigned User", flex: 1.2 },
    { field: "dueDate", headerName: "Due Date", flex: 0.9 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() =>
            navigate(`/tasks/edit/${row.id}`, {
              state: { redirectPath: "/manageTasks" },
            })
          }
          onDelete={() => handleOpenDeleteModal(row)}
          hideCreate
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="TASKS"
          subtitle={
            tenantName
              ? `Manage standalone tasks for ${tenantName}`
              : "Manage standalone tasks"
          }
        />
        <ActionButton
          tone="gold"
          onClick={() =>
            navigate("/createTask", {
              state: { redirectPath: "/manageTasks" },
            })
          }
          disabled={!tenantId}
        >
          Create Task
        </ActionButton>
      </Box>

      {!tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view tasks.
          </Typography>
        </Box>
      ) : (
        <ThemedDataGrid rows={tasks} columns={columns} />
      )}

      <DeleteTaskModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        task={selectedTask}
      />
    </Box>
  );
};

export default ManageTasks;